import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOData from '@/components/feature/SEOData';
import { useMemo } from 'react';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === slug),
    [slug]
  );

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">
              分类未找到
            </h1>
            <Link
              to="/"
              className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOData
        pageTitle={`${category.name} - 妈妈的朋友在线观看·完整版高清免费播放`}
        pageDescription={`妈妈的朋友${category.name}板块，${category.description}。浏览最新${category.name}内容，发现精彩影视作品。`}
        canonicalPath={`/category/${category.slug}`}
        pageType="category"
        breadcrumbs={[
          { name: '首页', url: 'https://mamadepengyou.com' },
          { name: category.name, url: `https://mamadepengyou.com/category/${category.slug}` },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        <section className="py-10 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs" />
              <span className="text-foreground-800">{category.name}</span>
            </nav>

            <div className="mb-10">
              <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 mb-3">
                {category.name}
              </h1>
              <p className="text-foreground-600">{category.description}</p>
            </div>

            <div className="flex flex-wrap gap-3 mb-10">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    c.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {c.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 transition-all duration-300 hover:shadow-md cursor-pointer"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                          {article.tags[0]}
                        </span>
                        <span className="text-xs text-foreground-500">
                          {article.readTime}
                        </span>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-900 leading-snug mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="mt-3 text-xs text-foreground-400">
                        {article.author} · {article.publishDate}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}